import { NEFT_BANK_ROWS } from './banks-data';

/**
 * The bank a GST challan is paid from by NEFT or RTGS (Phase 11). A NEFT/RTGS challan names it (its
 * bank_cd: the four letters its IFSCs start with), and the portal takes only banks on its own list
 * (banks-data.ts, the portal's NEFT bank master). People say it the way they say it at home: "HDFC",
 * "SBI", "state bank", "pay, hdfc se".
 */

export interface NeftBank {
  /** The four letters its IFSCs start with, e.g. HDFC, SBIN. */
  code: string;
  /** How EkApp shows it, e.g. "State Bank of India". */
  name: string;
  /** How the GST portal lists it, e.g. "STATE BANK OF INDIA". */
  portalName: string;
}

/** RBI's own codes: a business can't pay its GST from RBI. */
const NOT_A_PAYER = new Set(['RBIN', 'RBIP', 'RBIS']);

export const NEFT_BANKS: readonly NeftBank[] = NEFT_BANK_ROWS.filter(([code]) => !NOT_A_PAYER.has(code)).map(
  ([code, name, portalName]) => ({ code, name, portalName }),
);

const BY_CODE = new Map(NEFT_BANKS.map((b) => [b.code, b]));

/** The bank with this code, if the portal lists it. */
export function neftBank(code: string): NeftBank | null {
  return BY_CODE.get(code.trim().toUpperCase()) ?? null;
}

/** The banks most people pay from, offered first (as chips). */
export const COMMON_NEFT_BANKS: readonly string[] = ['HDFC', 'SBIN', 'ICIC'];

/** What people call the big banks, and the code each means (the first match wins). */
const ALIASES: readonly (readonly [pattern: RegExp, code: string])[] = [
  [/\bhdfc\b/, 'HDFC'],
  [/\b(?:sbi|state bank(?: of india)?)\b/, 'SBIN'],
  [/\bicici\b/, 'ICIC'],
  [/\baxis\b/, 'UTIB'],
  [/\bkotak\b/, 'KKBK'],
  [/\b(?:pnb|punjab national)\b/, 'PUNB'],
  [/\b(?:bob|bank of baroda|baroda)\b/, 'BARB'],
  [/\bcanara\b/, 'CNRB'],
  [/\b(?:union bank|ubi)\b/, 'UBIN'],
  // Before "bank of india" and "indian bank", which their names contain.
  [/\b(?:central bank|cbi)\b/, 'CBIN'],
  [/\b(?:iob|indian overseas)\b/, 'IOBA'],
  [/\bsouth indian\b/, 'SIBL'],
  [/\b(?:boi|bank of india)\b/, 'BKID'],
  [/\bindian bank\b/, 'IDIB'],
  [/\buco\b/, 'UCBA'],
  [/\b(?:bank of maharashtra|mahabank)\b/, 'MAHB'],
  [/\bpunjab (?:and|&) sind\b/, 'PSIB'],
  [/\bidbi\b/, 'IBKL'],
  [/\byes bank\b/, 'YESB'],
  [/\bindusind\b/, 'INDB'],
  [/\bidfc\b/, 'IDFB'],
  [/\bfederal\b/, 'FDRL'],
  [/\bkarnataka bank\b/, 'KARB'],
  [/\b(?:kvb|karur vysya)\b/, 'KVBL'],
  [/\brbl\b/, 'RATN'],
  [/\bau (?:small finance )?bank\b/, 'AUBL'],
  [/\b(?:cub|city union)\b/, 'CIUB'],
  [/\b(?:tmb|tamilnad mercantile)\b/, 'TMBL'],
  [/\bdcb\b/, 'DCBB'],
  [/\bbandhan\b/, 'BDBL'],
  [/\b(?:j ?& ?k|jk bank|jammu (?:and|&) kashmir)\b/, 'JAKA'],
  [/\bsaraswat\b/, 'SRCB'],
  [/\bcosmos\b/, 'COSB'],
  [/\bhsbc\b/, 'HSBC'],
  [/\bstandard chartered\b/, 'SCBL'],
  [/\bciti(?:bank)?\b/, 'CITI'],
  [/\bdbs\b/, 'DBSS'],
  [/\bdhanlaxmi\b/, 'DLXB'],
  [/\bequitas\b/, 'ESFB'],
  [/\bujjivan\b/, 'UJVN'],
  [/\bairtel\b/, 'AIRP'],
  [/\bpaytm\b/, 'PYTM'],
  [/\b(?:ippb|india post)\b/, 'IPOS'],
  [/\bjio\b/, 'JIOP'],
];

/** Words around a bank's name that aren't it: "pay, hdfc se", "from my SBI account". */
const FILLER =
  /\b(?:pay(?:ing)?|paid|file|from|frm|via|through|using|with|my|mera|meri|mere|apna|apne|se|say|ka|ke|ki|ko|me|mein|account|a\/c|acct|ac|net ?banking|neft|rtgs|karunga|karungi|karenge|karo|kar|do|dunga|dungi|bharunga|bharo|bhar|jama|i|will|ll|the|bank|ltd|limited|of|and)\b/g;

/** Lower case, "&" kept, everything else that isn't a letter or digit a space. */
const normalise = (text: string) =>
  text
    .toLowerCase()
    .normalize('NFKC')
    .replace(/co-?op(?:erative)?\.?/g, 'cooperative')
    .replace(/[^a-z0-9&]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * The banks someone could mean, best first: a name people use ("SBI", "pay, hdfc se") is that bank;
 * otherwise every word left must be in the bank's name ("saraswat", "kalupur commercial"). The same
 * bank under two codes is offered once.
 */
export function findNeftBanks(text: string, limit = 4): NeftBank[] {
  const said = normalise(text);
  if (!said) return [];
  for (const [pattern, code] of ALIASES) {
    if (pattern.test(said)) {
      const bank = BY_CODE.get(code);
      if (bank) return [bank];
    }
  }
  const words = said.replace(FILLER, ' ').split(' ').filter(Boolean);
  if (!words.length) return [];
  const scored: { bank: NeftBank; score: number }[] = [];
  for (const bank of NEFT_BANKS) {
    const name = ` ${normalise(bank.portalName)} ${normalise(bank.name)} `;
    if (!words.every((w) => name.includes(` ${w}`))) continue;
    const starts = normalise(bank.name).startsWith(words.join(' ')) ? 0 : 1;
    scored.push({ bank, score: starts * 1000 + bank.name.length });
  }
  scored.sort((a, b) => a.score - b.score);
  const seen = new Set<string>();
  const found: NeftBank[] = [];
  for (const { bank } of scored) {
    const key = bank.name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    found.push(bank);
    if (found.length >= limit) break;
  }
  return found;
}
